import { z } from "zod";

import { ChannelPlatformSchema, ChannelThreadSchema } from "./channels.js";
import { ChatEntrySchema } from "./chat.js";
import { ApprovalStatusSchema } from "./guards.js";
import { AttachmentSchema, ToolResultSchema } from "./parts.js";
import { ToolNameSchema } from "./tool-name.js";

export const UsageSchema = z.object({
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  cachedInputTokens: z.number().int().nonnegative().optional(),
  reasoningTokens: z.number().int().nonnegative().optional(),
}).strict();
export type Usage = z.infer<typeof UsageSchema>;

const sessionId = z.string().min(1);

/**
 * One of the box's screens as it belongs to an agent (ARCHITECTURE §6.15):
 * which display, what to call it in the pane's strip, and whether the agent is
 * on it right now. A screen stays the agent's after it lets go, so its pane
 * still shows what it left there; `busy` is what the dot marks.
 */
export const AgentScreenSchema = z.object({ display: z.string().min(1), title: z.string().min(1), busy: z.boolean() }).strict();
export type AgentScreen = z.infer<typeof AgentScreenSchema>;

/**
 * The single SSE vocabulary. The UI renders purely from these (ARCHITECTURE §5.6):
 * `chat.*` events are the bubbles, everything else feeds the conversation
 * outline (what the model did) and the agent's status.
 */
export const ServerEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("turn.started"), turnId: z.string().min(1), sessionId }).strict(),
  // A user-role entry reached the model transcript. `hidden` marks a prompt the
  // host wrote (a background task's report, a reply nudge, the first-turn cue):
  // it shows in the outline, never as a bubble.
  z.object({ type: z.literal("user.message"), sessionId, turnId: z.string().min(1), text: z.string(), attachments: z.array(AttachmentSchema).optional(), hidden: z.boolean().optional() }).strict(),
  z.object({ type: z.literal("chat.entry"), entry: ChatEntrySchema }).strict(),
  z.object({ type: z.literal("chat.entry.updated"), entry: ChatEntrySchema }).strict(),
  // A turn's prompt was taken back because the account paying for the model
  // may not spend (`code`, such as `balance_negative`): its entries are out of
  // the transcript and its bubbles (`chatIds`, none for a routine's hidden
  // prompt) out of the chat. `message` is why, for the page to say once, near
  // `at`; the turn's outline items go with it.
  z.object({ type: z.literal("prompt.withdrawn"), sessionId, turnId: z.string().min(1), chatIds: z.array(z.string().min(1)), message: z.string(), code: z.string().min(1), at: z.number().int().nonnegative() }).strict(),
  z.object({ type: z.literal("text.delta"), sessionId, text: z.string() }).strict(),
  z.object({ type: z.literal("reasoning.delta"), sessionId, text: z.string() }).strict(),
  z.object({ type: z.literal("tool.started"), sessionId, callId: z.string().min(1), name: ToolNameSchema, args: z.unknown() }).strict(),
  z.object({ type: z.literal("tool.progress"), sessionId, callId: z.string().min(1), chunk: z.string() }).strict(),
  z.object({ type: z.literal("tool.finished"), sessionId, callId: z.string().min(1), result: ToolResultSchema }).strict(),
  // `callId` is the parent's `task` call, so the UI can nest the child under it.
  z.object({ type: z.literal("session.spawned"), sessionId, parentId: sessionId, subagent: z.string().min(1), callId: z.string().min(1).optional(), title: z.string().optional() }).strict(),
  z.object({ type: z.literal("session.finished"), sessionId, summary: z.string(), status: z.enum(["completed", "error", "interrupted"]).optional() }).strict(),
  // A guard asked: the call waits on the card until the user answers or it expires (ARCHITECTURE §5.4).
  z.object({ type: z.literal("approval.requested"), approvalId: z.string().min(1), sessionId, callId: z.string().min(1), tool: ToolNameSchema, reason: z.string().min(1), summary: z.string().min(1), detail: z.string().optional(), proposedRule: z.string().min(1).optional(), expiresAt: z.number().int().nonnegative().optional() }).strict(),
  z.object({ type: z.literal("approval.resolved"), approvalId: z.string().min(1), status: ApprovalStatusSchema.exclude(["pending"]) }).strict(),
  // The session's earlier entries were condensed into a summary the model now reads first (ARCHITECTURE §6.6).
  // `context`: the prompt size it leaves, estimated until the next call reports the real one, so the gauge drops with it.
  z.object({ type: z.literal("context.condensed"), sessionId, condensedEntries: z.number().int().nonnegative(), epoch: z.number().int().positive(), context: z.object({ inputTokens: z.number().int().nonnegative(), outputTokens: z.number().int().nonnegative() }).strict().optional() }).strict(),
  // Who is taking their turn in a group chat right now, so the room's status
  // line names the member and not the room (ARCHITECTURE §6.8). `member` is
  // null once they are done and nobody is speaking.
  z.object({ type: z.literal("room.speaker"), member: z.object({ id: z.string().min(1), name: z.string().min(1) }).strict().nullable() }).strict(),
  // A routine was created, changed, deleted, or has just run (ARCHITECTURE
  // §6.9). The list is small and the UI refetches it rather than patching a
  // copy, so the event carries nothing but the fact that it moved.
  z.object({ type: z.literal("routines.changed") }).strict(),
  // A skill was written, changed or deleted (ARCHITECTURE §6.10); the panel
  // refetches the folder rather than tracking a copy of it.
  z.object({ type: z.literal("skills.changed") }).strict(),
  // The agent's task list was written (todos.ts); the panel refetches it.
  z.object({ type: z.literal("todos.changed") }).strict(),
  // A fact was learned, forgotten or tidied (memory.ts); the panel refetches.
  z.object({ type: z.literal("memory.changed") }).strict(),
  // The box's screens this agent has (ARCHITECTURE §6.15). Its pane shows
  // these and nothing else, so switching agents switches desktops rather than
  // showing everyone the same shared pair. Sent whole rather than as a delta:
  // there are only ever a handful, and one agent taking a screen changes two
  // agents' lists at once.
  z.object({ type: z.literal("screens"), screens: z.array(AgentScreenSchema) }).strict(),
  // A channel's link or settings moved (ARCHITECTURE §6.19): a new code to
  // scan, linked, logged out. The pane refetches the view; the event carries
  // only which channel.
  z.object({ type: z.literal("channel.changed"), platform: ChannelPlatformSchema }).strict(),
  // Someone wrote on a channel, or the agent answered them: the thread as it
  // now stands, so the list reorders without asking again.
  z.object({ type: z.literal("channel.thread"), thread: ChannelThreadSchema }).strict(),
  // `context`: the last call's prompt and answer sizes, so the page can show how full the window is without asking again.
  z.object({ type: z.literal("turn.finished"), turnId: z.string().min(1), usage: UsageSchema, context: z.object({ inputTokens: z.number().int().nonnegative(), outputTokens: z.number().int().nonnegative() }).strict().optional() }).strict(),
  z.object({ type: z.literal("error"), sessionId: sessionId.optional(), message: z.string(), retryable: z.boolean() }).strict(),
]);
export type ServerEvent = z.infer<typeof ServerEventSchema>;
